import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import moment from "moment";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import { TwMainLayout } from "../../../components/tailwind";
import { classNames } from "../../../helpers";
import { useToast } from "../../../provider/ToastProvider";
import { setSectionLoading } from "../../../redux/reducers/loadingReducer";
import { userState } from "../../../redux/reducers/userReducer";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForBookings, getUrlForClasses, getUrlForUsers } from "../../../utils/confighelpers";
import { FcConfirmDialog, Icon } from "../schedule/ui";
import { CancelSubscriptionModal, FreeTrialCreditModal, GiveCreditsModal, RemovePassModal } from "./MemberActionModals";
import MemberFormDrawer from "./MemberFormDrawer";
import { BookingsTab, InvoicesTab, ProductsTab, TransactionsTab } from "./MemberTabs";
import { apiResult, Avatar, Badge, EnvelopeIcon, euro, fmtDate, LoginMethod, phoneOf, PhoneIcon, purchaseName, purchaseStatus, resendWelcomeEmail, WhatsAppIcon, whatsappNumber } from "./memberUtils";
import SellProductDrawer from "./SellProductDrawer";
import MakeMemberModal from "./MakeMemberModal";
import "../../../styles/onboarding.css";

/**
 * Member page (new design). Route /mymembers/detail?id=<consumerId>&mid=<clubMemberId>.
 * Data: users/getUserDetail (person), getClubInviteCode?inviteId (club membership, Stripe customer),
 * bookings/getMyPurchases (free / classpass / subscription), getMybookings (next booking).
 */

const TABS = [
  ["products", "Passes & subscriptions"],
  ["bookings", "Bookings"],
  ["transactions", "Transaction history"],
  ["invoices", "Invoices"],
];
const EMPTY_PURCHASES = { free: [], classpass: [], subscription: [] };

export default function MemberDetailPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { addSuccessToast, addErrorToast } = useToast();
  const userReduxState = useSelector(userState);
  const businessId = userReduxState.userData?._id;
  const [params] = useSearchParams();
  const consumerId = params.get("id");
  const clubMemberId = params.get("mid");

  const [consumer, setConsumer] = React.useState(null);
  const [membership, setMembership] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [purchases, setPurchases] = React.useState(EMPTY_PURCHASES);
  const [purchasesLoading, setPurchasesLoading] = React.useState(true);
  const [nextBooking, setNextBooking] = React.useState(undefined);
  const [tab, setTab] = React.useState("products");
  const [tabKey, setTabKey] = React.useState(0); // remount money tabs after a change
  const [modal, setModal] = React.useState({ kind: "", purchase: null });
  const [removeBusy, setRemoveBusy] = React.useState(false);
  const [resendBusy, setResendBusy] = React.useState(false);
  const [lastSent, setLastSent] = React.useState(null);
  const [value, setValue] = React.useState(undefined); // member value: { paid, refunded, count } or null

  React.useEffect(() => {
    dispatch(setSectionLoading(false)); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!consumerId || !clubMemberId) navigate("/mymembers", { replace: true });
  }, [consumerId, clubMemberId, navigate]);

  const loadPerson = React.useCallback(async () => {
    if (!consumerId || !clubMemberId) return;
    setLoading(true);
    try {
      const [u, m] = await Promise.all([
        axiosInstance({ method: "get", url: `${getUrlForUsers()}getUserDetail?customerId=${consumerId}` }),
        axiosInstance({ method: "get", url: `${getUrlForClasses("")}getClubInviteCode?inviteId=${clubMemberId}` }),
      ]);
      setConsumer(u?.data?.data || null);
      setMembership(m?.data?.data || null);
    } catch (e) {
      setConsumer(null);
    } finally {
      setLoading(false);
    }
  }, [consumerId, clubMemberId]);

  const loadPurchases = React.useCallback(async () => {
    if (!consumerId || !businessId) return;
    setPurchasesLoading(true);
    const fetchAll = async (listOf) => {
      const out = [];
      for (let page = 1; page <= 10; page++) {
        const res = await axiosInstance({ method: "get", url: `${getUrlForBookings()}getMyPurchases?page=${page}&listOf=${listOf}&consumerId=${consumerId}&businessId=${businessId}` });
        const [ok, data] = apiResult(res);
        const list = ok && Array.isArray(data?.data) ? data.data : [];
        out.push(...list);
        if (!list.length || out.length >= (parseInt(data?.total, 10) || 0)) break;
      }
      return out;
    };
    try {
      const [free, classpass, subscription] = await Promise.all(["free", "classpass", "subscription"].map((k) => fetchAll(k).catch(() => [])));
      const order = (a, b) => (purchaseStatus(b).active - purchaseStatus(a).active) || moment(b.purchaseDate || b.createdAt).diff(moment(a.purchaseDate || a.createdAt));
      setPurchases({ free: free.sort(order), classpass: classpass.sort(order), subscription: subscription.sort(order) });
    } finally {
      setPurchasesLoading(false);
    }
  }, [consumerId, businessId]);

  const loadNextBooking = React.useCallback(async () => {
    if (!consumerId || !businessId) return;
    try {
      const res = await axiosInstance({
        method: "get",
        url: `${getUrlForBookings()}getMybookings?teacherId=${businessId}&studentId=${consumerId}&page=1&limit=20&filterBy=upcoming_confirmed&listFor=${userReduxState.businessType}`,
      });
      const [ok, data] = apiResult(res);
      const list = (ok && Array.isArray(data?.data) ? data.data : []).filter((b) => b.status !== "Cancelled");
      const when = (b) => moment(b.schedule_class_detail?.classDate || b.activityDate);
      list.sort((a, b) => when(a).diff(when(b)));
      setNextBooking(list.find((b) => when(b).isAfter(moment())) || null);
    } catch (e) {
      setNextBooking(null);
    }
  }, [consumerId, businessId, userReduxState.businessType]);

  /** Member value: all successful payments (card, iDEAL, invoices, cash invoices) minus refunds, from the same list as Transaction history. */
  const loadValue = React.useCallback(async () => {
    if (!clubMemberId) return;
    try {
      const res = await axiosInstance({ method: "post", url: `${getUrlForBookings()}getMembersAllPayments`, data: { clubMemberId } });
      const [ok, data] = apiResult(res);
      const rows = ok && Array.isArray(data) ? data : [];
      let paid = 0;
      let refunded = 0;
      let count = 0;
      rows.forEach((r) => {
        const st = r?.payment_intent?.status || r?.status;
        if (st !== "succeeded" && st !== "paid") return;
        const amount = parseFloat(r.amount_total) || 0;
        const ch = r?.payment_intent?.charges?.data?.[0];
        paid += amount;
        count += 1;
        refunded += ch?.refunded ? amount : (ch?.amount_refunded || 0) / 100;
      });
      setValue({ paid, refunded, count });
    } catch (e) {
      setValue(null);
    }
  }, [clubMemberId]);

  React.useEffect(() => {
    loadPerson();
  }, [loadPerson]);
  React.useEffect(() => {
    loadValue();
  }, [loadValue]);
  React.useEffect(() => {
    loadPurchases();
    loadNextBooking();
  }, [loadPurchases, loadNextBooking]);

  const refreshMoney = () => {
    loadPurchases();
    loadValue();
    setTabKey((k) => k + 1);
  };

  const first = (consumer?.fullName || "").split(" ")[0] || "This member";
  const removed = membership?.clubMemberStatus === "removed" || membership?.status === "removed";
  /** Booked a free trial class (/trial): not a member yet, no payment profile. "Add to members" converts them. */
  const isGuest = !removed && membership?.clubMemberStatus === "guest";
  /** Hasn't chosen a password yet (club-added, or a guest just made a member; a guest is SMS-verified but has none). */
  const accountActive = consumer?.isVerified === true && membership?.accountActivated !== false;
  const status = removed
    ? { label: "Removed", tone: "red" }
    : isGuest
      ? { label: "Guest", tone: "blue" }
      : accountActive ? { label: "Active", tone: "green" } : { label: "Not activated", tone: "amber" };
  const memberRow = consumer ? { consumer_detail: { ...consumer, consumerId: consumer._id || consumerId } } : null;

  // summary tiles
  const activePasses = [...purchases.classpass, ...purchases.free].filter((p) => purchaseStatus(p).active);
  const creditsLeft = activePasses.reduce((n, p) => n + Math.max(0, parseInt(p.total_voucher_unused, 10) || 0), 0);
  const mainPass = activePasses.slice().sort((a, b) => (parseInt(b.total_voucher_unused, 10) || 0) - (parseInt(a.total_voucher_unused, 10) || 0))[0];
  const activeSub = purchases.subscription.find((p) => purchaseStatus(p).active);
  const isUnlimitedSub = (p) => p?.selectedPassDetail?.unlimitedCredits !== false || String(p?.selectedPassDetail?.noOfAllowedSessions) === "unlimited";
  const activeSubs = purchases.subscription.filter((p) => purchaseStatus(p).active);
  const unlimitedSub = activeSubs.find(isUnlimitedSub);
  const subCredits = activeSubs.filter((p) => !isUnlimitedSub(p)).reduce((n, p) => n + Math.max(0, parseInt(p.total_voucher_unused, 10) || 0), 0);
  const totalCredits = creditsLeft + subCredits;
  const nextWhen = nextBooking ? moment(nextBooking.schedule_class_detail?.classDate || nextBooking.activityDate) : null;
  const productCount = purchases.free.length + purchases.classpass.length + purchases.subscription.length;

  const removeMember = async () => {
    setRemoveBusy(true);
    try {
      const res = await axiosInstance({ method: "post", url: `${getUrlForBookings()}removeMember`, data: { consumerId } });
      const [ok, , msg] = apiResult(res);
      if (ok) {
        addSuccessToast(`${consumer?.fullName || "Member"} removed`);
        navigate("/mymembers");
      } else addErrorToast(msg || "Couldn't remove this member.");
    } catch (e) {
      addErrorToast("Couldn't remove this member.");
    } finally {
      setRemoveBusy(false);
    }
  };

  const resend = async () => {
    setResendBusy(true);
    const [ok, msg] = await resendWelcomeEmail(consumerId);
    setResendBusy(false);
    if (ok) {
      setLastSent(new Date());
      addSuccessToast(msg);
    } else addErrorToast(msg);
  };
  const notActivated = !loading && !removed && !isGuest && consumer && !accountActive;
  const openMakeMember = (reason = "") => setModal({ kind: "makeMember", reason });
  const sentAt = lastSent || membership?.welcomeEmailSentAt || null;

  const phone = phoneOf(consumer);
  const wa = whatsappNumber(consumer);

  return (
    <TwMainLayout
      children={
        <div className="flex-1 px-4 pb-16 pt-6 sm:px-8 lg:px-10">
          <button type="button" onClick={() => navigate("/mymembers")} className="flex items-center gap-1.5 text-sm font-extrabold text-gray-600 hover:text-gray-900">
            <Icon className="h-4 w-4" d={<path d="m15 18-6-6 6-6" />} />
            My Members
          </button>

          {/* Profile */}
          <section className="mt-4 rounded-3xl border border-gray-200 bg-white p-5 shadow-[0_10px_30px_rgba(72,27,58,0.05)] sm:p-6">
            {loading ? (
              <div className="flex animate-pulse items-center gap-5" aria-hidden="true">
                <span className="h-[72px] w-[72px] rounded-full bg-[#EDE5F0]" />
                <span className="flex-1">
                  <span className="block h-6 w-56 rounded-full bg-[#EDE5F0]" />
                  <span className="mt-3 block h-4 w-72 max-w-full rounded-full bg-[#F3EEF5]" />
                </span>
              </div>
            ) : (
              <div className="flex flex-col gap-5 lg:flex-row lg:items-center">
                <div className="flex min-w-0 flex-1 items-center gap-4 sm:gap-5">
                  <Avatar src={consumer?.profilePic} name={consumer?.fullName} size={72} muted={removed} />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <h1 className="text-2xl font-black tracking-tight text-gray-900 sm:text-[28px]">{consumer?.fullName || "Member"}</h1>
                      <Badge tone={status.tone}>{status.label}</Badge>
                    </div>
                    <p className="mt-1 text-sm text-gray-600">
                      {isGuest ? "Trial guest since" : "Member since"} {fmtDate(isGuest ? membership?.guestSince || membership?.createdAt : membership?.createdAt) || "–"}
                      {isGuest && membership?.heardFrom ? ` · Heard about you via ${membership.heardFrom}` : ""} · Last active{" "}
                      {consumer?.isVerified === true ? fmtDate(membership?.lastActivity || consumer?.lastActivity) || "–" : "never"}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm font-bold text-gray-800">
                      {phone && (
                        <span className="flex items-center gap-1.5">
                          <PhoneIcon aria-hidden="true" className="size-4 shrink-0 text-gray-400" />
                          {phone}
                        </span>
                      )}
                      {consumer?.email && (
                        <span className="flex min-w-0 items-center gap-1.5">
                          <EnvelopeIcon aria-hidden="true" className="size-4 shrink-0 text-gray-400" />
                          <span className="truncate">{consumer.email}</span>
                        </span>
                      )}
                      <LoginMethod user={consumer} />
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {wa && (
                    <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" className="flex h-11 items-center gap-2 rounded-2xl border border-gray-200 bg-white px-4 text-sm font-extrabold text-gray-900 hover:bg-gray-50">
                      <WhatsAppIcon className="size-5 text-gray-400" />
                      WhatsApp
                    </a>
                  )}
                  {consumer?.email && (
                    <a href={`mailto:${consumer.email}`} className="flex h-11 items-center gap-2 rounded-2xl border border-gray-200 bg-white px-4 text-sm font-extrabold text-gray-900 hover:bg-gray-50">
                      <EnvelopeIcon aria-hidden="true" className="size-5 text-gray-400" />
                      Email
                    </a>
                  )}
                  {!removed && (
                    <Menu as="div" className="relative">
                      <MenuButton aria-label="More member options" className="flex h-11 w-11 items-center justify-center rounded-2xl border border-gray-200 bg-white text-gray-600 hover:bg-gray-50">
                        <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true"><circle cx="12" cy="5" r="1.6" fill="currentColor" /><circle cx="12" cy="12" r="1.6" fill="currentColor" /><circle cx="12" cy="19" r="1.6" fill="currentColor" /></svg>
                      </MenuButton>
                      <MenuItems anchor="bottom end" className="z-[60] mt-1 w-56 rounded-2xl border border-gray-200 bg-white p-1.5 shadow-xl focus:outline-none">
                        <MenuItem>
                          <button type="button" onClick={() => setModal({ kind: "edit" })} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-gray-900 data-[focus]:bg-[#F7F4F8]">
                            <Icon className="h-4 w-4 text-gray-500" d={<><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></>} />
                            Edit details
                          </button>
                        </MenuItem>
                        {isGuest && (
                          <MenuItem>
                            <button type="button" onClick={() => openMakeMember()} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-gray-900 data-[focus]:bg-[#F7F4F8]">
                              <Icon className="h-4 w-4 text-gray-500" d={<><circle cx="9" cy="8" r="4" /><path d="M2 21a7 7 0 0 1 14 0M19 8v6M16 11h6" /></>} />
                              Add to members
                            </button>
                          </MenuItem>
                        )}
                        {notActivated && (
                          <MenuItem>
                            <button type="button" onClick={resend} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-gray-900 data-[focus]:bg-[#F7F4F8]">
                              <EnvelopeIcon aria-hidden="true" className="size-4 text-gray-400" />
                              Resend welcome email
                            </button>
                          </MenuItem>
                        )}
                        <MenuItem>
                          <button type="button" onClick={() => setModal({ kind: "removeMember" })} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-[#B4232F] data-[focus]:bg-[#FFF5F6]">
                            <Icon className="h-4 w-4" d={<><circle cx="9" cy="8" r="4" /><path d="M2 21a7 7 0 0 1 14 0M16 11h6" /></>} />
                            Remove member
                          </button>
                        </MenuItem>
                      </MenuItems>
                    </Menu>
                  )}
                </div>
              </div>
            )}
          </section>

          {isGuest && (
            <section className="mt-4 flex flex-col gap-3 rounded-3xl border border-[#C9DAFB] bg-[#F2F6FF] p-4 sm:flex-row sm:items-center sm:p-5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#E7F0FF] text-[#1D4ED8]">
                <Icon className="h-5 w-5" d={<><circle cx="9" cy="8" r="4" /><path d="M2 21a7 7 0 0 1 14 0M19 8v6M16 11h6" /></>} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[15px] font-extrabold text-gray-900">{first} is a trial guest</div>
                <div className="mt-0.5 text-[13px] text-gray-600">
                  They booked a free trial class{membership?.heardFrom ? ` (heard about you via ${membership.heardFrom})` : ""}. They're not a member yet: add them to your members to sell passes or subscriptions.
                </div>
              </div>
              <button
                type="button"
                onClick={() => openMakeMember()}
                className="flex h-11 shrink-0 items-center justify-center gap-2 rounded-2xl bg-brown-coffee px-4 text-sm font-extrabold text-white hover:bg-light-brown-coffee"
              >
                Add to members
              </button>
            </section>
          )}

          {notActivated && (
            <section className="mt-4 flex flex-col gap-3 rounded-3xl border border-[#F5D9A8] bg-[#FFF8EC] p-4 sm:flex-row sm:items-center sm:p-5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#FFF4E5] text-[#7A4A00]">
                <EnvelopeIcon aria-hidden="true" className="size-5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[15px] font-extrabold text-gray-900">{first} hasn't activated their account yet</div>
                <div className="mt-0.5 text-[13px] text-gray-600">
                  Their welcome email with the login details for the FitClub app went to <span className="font-bold text-gray-800">{consumer?.email || "no email address"}</span>
                  {sentAt ? ` · last sent ${moment(sentAt).format("D MMM, HH:mm")}` : ""}. Wrong address?{" "}
                  <button type="button" onClick={() => setModal({ kind: "edit" })} className="font-extrabold text-indigo-bz hover:underline">Edit details</button> first.
                </div>
              </div>
              <button
                type="button"
                onClick={resend}
                disabled={resendBusy || !consumer?.email}
                className="flex h-11 shrink-0 items-center justify-center gap-2 rounded-2xl bg-brown-coffee px-4 text-sm font-extrabold text-white hover:bg-light-brown-coffee disabled:opacity-60"
              >
                {resendBusy && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                Resend welcome email
              </button>
            </section>
          )}

          {/* Summary tiles */}
          <div className="mt-4 grid gap-3 sm:grid-cols-2 sm:gap-4 xl:grid-cols-4">
            <Tile label="Credits left" loading={purchasesLoading} empty={!unlimitedSub && !totalCredits}
              value={unlimitedSub ? "Unlimited" : totalCredits ? String(totalCredits) : "No credits"} big={!!unlimitedSub || !!totalCredits}
              sub={
                unlimitedSub
                  ? `with ${purchaseName(unlimitedSub)}${creditsLeft ? ` · plus ${creditsLeft} on passes` : ""}`
                  : creditsLeft
                  ? `on ${purchaseName(mainPass)}${mainPass?.selectedPassDetail?.validUntilConsumed ? "" : mainPass?.purchaseExpiredDate ? ` · valid until ${fmtDate(mainPass.purchaseExpiredDate, "D MMM")}` : ""}${subCredits ? ` · plus ${subCredits} this period` : ""}`
                  : subCredits
                  ? `this period on ${purchaseName(activeSubs.find((p) => !isUnlimitedSub(p)))}`
                  : "Add a pass or free credits"
              } />
            <Tile label="Subscription" loading={purchasesLoading} empty={!activeSub} value={activeSub ? purchaseName(activeSub) : "None"}
              sub={activeSub ? (purchaseStatus(activeSub).cancelling ? `Ends ${fmtDate(activeSub.purchaseExpiredDate)}` : `Renews ${fmtDate(activeSub.purchaseExpiredDate)}`) : "No active subscription"} />
            <Tile label="Next booking" loading={nextBooking === undefined} empty={!nextBooking} value={nextWhen ? nextWhen.format("ddd D MMM, HH:mm") : "None"}
              sub={nextBooking ? [nextBooking.className || nextBooking.class_detail?.className, nextBooking.schedule_class_detail?.location?.addressName].filter(Boolean).join(" · ") : "No upcoming bookings"} />
            <Tile
              label="Member value"
              loading={value === undefined}
              empty={!value || value.paid - value.refunded <= 0}
              value={value ? euro(Math.max(0, value.paid - value.refunded)) : "–"}
              big={!!value && value.paid - value.refunded > 0}
              sub={
                !value
                  ? "Couldn't load payments"
                  : value.count === 0
                    ? "No payments yet"
                    : `paid in total${(() => {
                        const months = Math.max(1, moment().diff(moment(membership?.createdAt || undefined), "months", true));
                        return membership?.createdAt ? ` · about ${euro((value.paid - value.refunded) / months)} a month` : "";
                      })()}${value.refunded > 0 ? ` · ${euro(value.refunded)} refunded` : ""}`
              }
            />
          </div>

          {/* Tabs */}
          <div role="tablist" aria-label="Member details" className="mt-6 flex gap-1 overflow-x-auto border-b border-gray-200">
            {TABS.map(([k, l]) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={tab === k}
                onClick={() => setTab(k)}
                className={classNames("-mb-px h-12 shrink-0 whitespace-nowrap border-b-[3px] px-4 text-[15px] font-extrabold", tab === k ? "border-portland-orange text-brown-coffee" : "border-transparent text-gray-500 hover:text-gray-900")}
              >
                {l}
                {k === "products" && !purchasesLoading && (
                  <span className={classNames("ml-1.5 rounded-full px-2 py-0.5 text-xs", tab === k ? "bg-[#FFE8EB] text-[#B4232F]" : "bg-[#F1EEF2] text-gray-500")}>{productCount}</span>
                )}
              </button>
            ))}
          </div>
          <div className="mt-5">
            {tab === "products" && (
              <ProductsTab
                purchases={purchases}
                loading={purchasesLoading}
                first={first}
                onFreeTrial={() => setModal({ kind: "trial" })}
                onMigration={() => setModal({ kind: "credits" })}
                onAddPass={() => (isGuest ? openMakeMember(`Add ${first} to your members first, then add a pass.`) : setModal({ kind: "sellPass" }))}
                onAddSubscription={() => (isGuest ? openMakeMember(`Add ${first} to your members first, then add a subscription.`) : setModal({ kind: "sellSub" }))}
                onCancelSub={(p) => setModal({ kind: "cancelSub", purchase: p })}
                onRemovePass={(p) => setModal({ kind: "removePass", purchase: p })}
              />
            )}
            {tab === "bookings" && <BookingsTab consumerId={consumerId} first={first} />}
            {tab === "transactions" && <TransactionsTab key={`t${tabKey}`} clubMemberId={clubMemberId} consumerId={consumerId} first={first} onChanged={refreshMoney} />}
            {tab === "invoices" && <InvoicesTab key={`i${tabKey}`} clubMemberId={clubMemberId} first={first} email={consumer?.email} onChanged={refreshMoney} />}
          </div>

          <MakeMemberModal
            open={modal.kind === "makeMember"}
            guest={consumer ? { ...consumer, consumerId } : null}
            reason={modal.reason}
            onClose={() => setModal({ kind: "" })}
            onDone={() => {
              loadPerson();
              refreshMoney();
            }}
          />
          <MemberFormDrawer open={modal.kind === "edit"} member={memberRow} onClose={() => setModal({ kind: "" })} onSaved={loadPerson} />
          <SellProductDrawer open={modal.kind === "sellPass"} productType="classpass" consumer={consumer} onClose={() => setModal({ kind: "" })} onDone={refreshMoney} />
          <SellProductDrawer open={modal.kind === "sellSub"} productType="subscription" consumer={consumer} onClose={() => setModal({ kind: "" })} onDone={refreshMoney} />
          <GiveCreditsModal open={modal.kind === "credits"} consumer={consumer} onClose={() => setModal({ kind: "" })} onDone={refreshMoney} />
          <FreeTrialCreditModal open={modal.kind === "trial"} consumer={consumer} onClose={() => setModal({ kind: "" })} onDone={refreshMoney} />
          <CancelSubscriptionModal open={modal.kind === "cancelSub"} purchase={modal.purchase} memberFirst={first} onClose={() => setModal({ kind: "" })} onDone={refreshMoney} />
          <RemovePassModal
            open={modal.kind === "removePass"}
            purchase={modal.purchase}
            consumerId={consumerId}
            stripeCustomer={membership?.stripeCustomerId}
            memberFirst={first}
            onClose={() => setModal({ kind: "" })}
            onDone={refreshMoney}
          />
          <FcConfirmDialog
            open={modal.kind === "removeMember"}
            danger
            busy={removeBusy}
            title={`Remove ${consumer?.fullName || "this member"}?`}
            message="They lose access to your club, including any credits and subscriptions they still have. This can't be undone."
            confirmLabel="Remove member"
            cancelLabel="Keep member"
            onClose={() => setModal({ kind: "" })}
            onConfirm={removeMember}
          />
        </div>
      }
    />
  );
}

function Tile({ label, value, sub, loading, empty, big }) {
  return (
    <div className="rounded-3xl border border-gray-200 bg-white px-5 py-4 shadow-[0_10px_30px_rgba(72,27,58,0.05)]">
      <div className="text-xs font-extrabold uppercase tracking-wide text-gray-500">{label}</div>
      {loading ? (
        <div className="mt-2 animate-pulse" aria-hidden="true">
          <div className="h-6 w-28 rounded-full bg-[#EDE5F0]" />
          <div className="mt-2 h-3 w-40 rounded-full bg-[#F3EEF5]" />
        </div>
      ) : (
        <>
          <div className={classNames("mt-1.5 truncate font-black", big ? "text-[26px] leading-tight" : "text-xl", empty ? "text-gray-400" : "text-gray-900")}>{value}</div>
          <div className="mt-0.5 truncate text-[13px] text-gray-500">{sub}</div>
        </>
      )}
    </div>
  );
}
